import { create } from 'zustand';
/**
 * Zustand store for managing chat artifacts
 * Provides centralized state management for artifacts in the current chat session
 */ export const useChatArtifactStore = create((set, get)=>({
        currentChatId: null,
        artifacts: new Map(),
        selectedArtifact: null,
        versions: new Map(),
        previewArtifact: null,
        setChatId: (chatId)=>{
            set({
                currentChatId: chatId,
                artifacts: new Map(),
                selectedArtifact: null,
                versions: new Map(),
                previewArtifact: null
            });
        },
        addArtifact: (artifact)=>{
            set((state)=>{
                const newArtifacts = new Map(state.artifacts);
                newArtifacts.set(artifact.id, artifact);
                return {
                    artifacts: newArtifacts
                };
            });
        },
        updateArtifact: (id, updates)=>{
            set((state)=>{
                const newArtifacts = new Map(state.artifacts);
                const existing = newArtifacts.get(id);
                if (!existing) {
                    return state;
                }
                // Only auto-update timestamp if not explicitly provided in updates
                const updated = {
                    ...existing,
                    ...updates,
                    updatedAt: updates.updatedAt ?? new Date().toISOString()
                };
                newArtifacts.set(id, updated);
                // If this is the selected artifact, update selectedArtifact too
                const newSelectedArtifact = state.selectedArtifact?.id === id ? updated : state.selectedArtifact;
                return {
                    artifacts: newArtifacts,
                    selectedArtifact: newSelectedArtifact
                };
            });
        },
        deleteArtifact: (id)=>{
            set((state)=>{
                const newArtifacts = new Map(state.artifacts);
                newArtifacts.delete(id);
                // Clear selection if deleting the selected artifact
                const newSelectedArtifact = state.selectedArtifact?.id === id ? null : state.selectedArtifact;
                return {
                    artifacts: newArtifacts,
                    selectedArtifact: newSelectedArtifact
                };
            });
        },
        selectArtifact: (id)=>{
            set((state)=>{
                if (id === null) {
                    return {
                        selectedArtifact: null
                    };
                }
                const artifact = state.artifacts.get(id);
                return {
                    selectedArtifact: artifact || null
                };
            });
        },
        getArtifact: (id)=>{
            const { artifacts } = get();
            return artifacts.get(id);
        },
        getArtifacts: ()=>{
            const { artifacts } = get();
            return Array.from(artifacts.values());
        },
        clearArtifacts: (chatId)=>{
            set((state)=>{
                // If chatId is provided, only clear if it matches current chat
                if (chatId && state.currentChatId !== chatId) {
                    return state;
                }
                return {
                    artifacts: new Map(),
                    selectedArtifact: null,
                    versions: new Map(),
                    previewArtifact: null
                };
            });
        },
        setVersions: (id, versions)=>{
            set((state)=>{
                const newVersions = new Map(state.versions);
                newVersions.set(id, versions);
                return {
                    versions: newVersions
                };
            });
        },
        getVersions: (id)=>{
            const { versions } = get();
            return versions.get(id);
        },
        previewVersion: (id, updates)=>{
            set((state)=>{
                const mainArtifact = state.artifacts.get(id);
                if (!mainArtifact) {
                    return state;
                }
                // Create a preview artifact that doesn't modify the main artifact
                const previewArtifact = {
                    ...mainArtifact,
                    ...updates,
                    previewForId: id
                };
                return {
                    previewArtifact
                };
            });
        },
        clearPreview: ()=>{
            set({
                previewArtifact: null
            });
        },
        getDisplayArtifact: (id)=>{
            const { artifacts, previewArtifact } = get();
            // If there's a preview for this artifact, return it
            if (previewArtifact && previewArtifact.previewForId === id) {
                return previewArtifact;
            }
            // Otherwise return the main artifact
            return artifacts.get(id);
        }
    }));
